import { Routes } from '@angular/router';
import { ShellComponent } from './layout/shell/shell.component';
import { authGuard } from './core/guards/auth.guard';
import { studioRoleGuard } from './core/guards/studio-role.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./features/login/login.component').then(
        (m) => m.LoginComponent
      ),
    title: 'Login — Invento Rating',
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./features/forgot-password/forgot-password.component').then(
        (m) => m.ForgotPasswordComponent
      ),
    title: 'Forgot Password — Invento Rating',
  },
  {
    path: 'reset-password/:userId/:email/:resetToken',
    loadComponent: () =>
      import('./features/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent
      ),
    title: 'Reset Password — Invento Rating',
  },
  {
    path: '',
    component: ShellComponent,
    canActivate: [authGuard, studioRoleGuard],
    canActivateChild: [authGuard, studioRoleGuard],
    children: [
      {
        path: 'dashboard',
        redirectTo: 'line-of-business',
        pathMatch: 'full',
      },
      {
        path: 'line-of-business',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent
          ),
        title: 'Lines of Business — Invento Rating',
      },
      // "Sub Part" is the user-facing name for what the backend still calls an
      // Algorithm. Only the UI was renamed — entities, tables and API routes are
      // unchanged. The old /algorithms paths redirect so saved links keep working.
      {
        path: 'sub-parts',
        loadComponent: () =>
          import('./features/algorithms/algorithms.component').then(
            (m) => m.AlgorithmsComponent
          ),
        title: 'Sub Parts — Invento Rating',
      },
      { path: 'algorithms', redirectTo: 'sub-parts', pathMatch: 'full' },
      {
        path: 'line-of-business/:lobId/sub-parts',
        loadComponent: () =>
          import('./features/lob-algorithms/lob-algorithms.component').then(
            (m) => m.LobAlgorithmsComponent
          ),
        title: 'Sub Parts — Invento Rating',
      },
      {
        path: 'line-of-business/:lobId/algorithms',
        redirectTo: 'line-of-business/:lobId/sub-parts',
        pathMatch: 'full',
      },
      {
        path: 'line-of-business/:id',
        loadComponent: () =>
          import('./features/lob-hub/lob-hub.component').then(
            (m) => m.LobHubComponent
          ),
        title: 'Algorithm — Invento Rating',
      },
      {
        path: 'line-of-business/:lobId/rule-builder',
        loadComponent: () =>
          import('./features/canvas/canvas.component').then(
            (m) => m.CanvasComponent
          ),
        data: { ownerType: 'lineOfBusiness' },
        title: 'Rule Builder — Invento Rating',
      },
      {
        path: 'line-of-business/:id/canvas',
        loadComponent: () =>
          import('./features/canvas/canvas.component').then(
            (m) => m.CanvasComponent
          ),
        data: { ownerType: 'algorithm' },
        title: 'Rule Builder — Invento Rating',
      },
      {
        path: 'line-of-business/:id/formula',
        loadComponent: () =>
          import('./features/formula-editor/formula-editor.component').then(
            (m) => m.FormulaEditorComponent
          ),
        title: 'Formula Editor — Invento Rating',
      },
      {
        path: 'line-of-business/:id/form-designer',
        loadComponent: () =>
          import('./features/form-designer/form-designer.component').then(
            (m) => m.FormDesignerComponent
          ),
        title: 'Form Designer — Invento Rating',
      },
      // ── Endorsement Studio — HIDDEN ──────────────────────────────────
      // Parked, not removed: the components, backend tables and API all stay
      // intact. It is hidden because "Endorsement" now also names a kind of Sub
      // Part, and having both on screen at once would be two different things
      // wearing the same word. Uncomment these two routes and the sidebar entry
      // in sidebar.component.ts to bring it back.
      // {
      //   path: 'line-of-business/:id/endorsements',
      //   loadComponent: () =>
      //     import('./features/endorsement-studio/endorsement-studio-list.component').then(
      //       (m) => m.EndorsementStudioListComponent
      //     ),
      //   title: 'Endorsement Studio — Invento Rating',
      // },
      // {
      //   path: 'line-of-business/:id/endorsements/:endorsementId',
      //   loadComponent: () =>
      //     import('./features/endorsement-studio/endorsement-editor-shell.component').then(
      //       (m) => m.EndorsementEditorShellComponent
      //     ),
      //   title: 'Endorsement Editor — Invento Rating',
      // },
      {
        path: 'line-of-business/:id/lookup-tables',
        loadComponent: () =>
          import('./features/lookup-tables/lookup-tables.component').then(
            (m) => m.LookupTablesComponent
          ),
        title: 'Lookup Tables — Invento Rating',
      },
      {
        path: 'line-of-business/:id/variables',
        loadComponent: () =>
          import('./features/variables/variables.component').then(
            (m) => m.VariablesComponent
          ),
        title: 'Variables — Invento Rating',
      },
      {
        path: 'line-of-business/:id/input-variables',
        loadComponent: () =>
          import('./features/input-variables/input-variables.component').then(
            (m) => m.InputVariablesComponent
          ),
        title: 'Input Variables — Invento Rating',
      },
      {
        path: 'decision-tree',
        loadComponent: () =>
          import('./features/decision-tree/decision-tree.component').then(
            (m) => m.DecisionTreeComponent
          ),
        title: 'Decision Tree — Invento Rating',
      },
      {
        path: 'simulator',
        loadComponent: () =>
          import('./features/simulator/simulator.component').then(
            (m) => m.SimulatorComponent
          ),
        title: 'Rating Simulator — Invento Rating',
      },
      {
        path: 'run-algorithm',
        loadComponent: () =>
          import('./features/run-algorithm/run-algorithm.component').then(
            (m) => m.RunAlgorithmComponent
          ),
        title: 'Calculate Premium — Invento Rating',
      },
      {
        path: 'version-compare',
        loadComponent: () =>
          import('./features/version-compare/version-compare.component').then(
            (m) => m.VersionCompareComponent
          ),
        title: 'Version Compare — Invento Rating',
      },
      {
        path: 'validation',
        loadComponent: () =>
          import('./features/validation/validation.component').then(
            (m) => m.ValidationComponent
          ),
        title: 'Validation — Invento Rating',
      },
      {
        path: 'csv-import',
        loadComponent: () =>
          import('./features/csv-import/csv-import.component').then(
            (m) => m.CsvImportComponent
          ),
        title: 'CSV Import — Invento Rating',
      },
      {
        path: 'audit-logs',
        loadComponent: () =>
          import('./features/audit-logs/audit-logs.component').then(
            (m) => m.AuditLogsComponent
          ),
        title: 'Audit Logs — Invento Rating',
      },
      {
        path: 'deployment',
        loadComponent: () =>
          import('./features/deployment/deployment.component').then(
            (m) => m.DeploymentComponent
          ),
        title: 'Deployment — Invento Rating',
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'login',
  },
];
